import { FC, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { EntityType } from '@/entities/vfo/types';
import { useNotices } from '@/features/notices/useNotices';
import { Checkbox } from '@/features/vfo/components/Checkbox';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { breakpoints, media } from '@/shared/components';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AddToCaseModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../../interwill/containers/ModalContainerV2';

export const AddToCaseModal: FC<AddToCaseModalProps> = (props) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'addToCase' });

  const [loading, setLoading] = useState(false);
  const [selectedCases, setSelectedCases] = useState<string[]>([]);

  const notices = useNotices();
  const { members } = useMembers();
  const { cases, addMember, attachDocument } = useCases();
  const { closeModal } = useModalsStore();

  const handleToggle = (id: string) => {
    setSelectedCases((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
  };

  const executeAndCheck = async (promises: Promise<object | boolean | null>[]) => {
    const results = await Promise.allSettled(promises);

    const hasErrors = results.some(
      (result) => result.status === 'rejected' || (result.status === 'fulfilled' && !result.value),
    );

    return !hasErrors;
  };

  const handleAdd = async () => {
    setLoading(true);
    if (props.entityType === EntityType.Member) {
      const member = members.find((item) => item.profileId === props.entityId);
      if (!member || !member.profileId) {
        setLoading(false);
        return;
      }

      const success = await executeAndCheck(
        selectedCases.map((item) => {
          if (member.profileId && member.tag) {
            return addMember({
              caseId: item,
              profileId: member.profileId,
              tag: member.tag,
              country: member.country,
            });
          }
          return Promise.reject();
        }),
      );
      setLoading(false);

      if (!success) {
        return;
      }

      notices.success(t('successNotice', { fullname: `${member.firstName} ${member.lastName}` }));
      closeModal();
      return;
    }

    const success = await executeAndCheck(
      selectedCases.map((item) => attachDocument(item, String(props.entityId))),
    );
    setLoading(false);
    if (!success) {
      return;
    }
    notices.success(t('successNoticeDocument'));
    closeModal();
  };

  const availableCases =
    props.entityType === EntityType.Member
      ? cases.filter(
          (item) =>
            !item.isPrivateCase && !item.members.some((m) => m.profileId === props.entityId),
        )
      : cases.filter((item) => !item.caseDocuments.some((d) => d.documentId === props.entityId));

  if (!availableCases.length) {
    return (
      <ModalContainerV2 pinBottom title={t('noCases')}>
        <ListContainer>
          <span>
            {t(props.entityType === EntityType.Member ? 'noCasesText' : 'noCasesTextDocument')}
          </span>
        </ListContainer>
        <Buttons>
          <Button size="middle" onClick={closeModal}>
            {t('cancel')}
          </Button>
        </Buttons>
      </ModalContainerV2>
    );
  }

  return (
    <ModalContainerV2
      pinBottom
      title={t(props.entityType === EntityType.Member ? 'title' : 'titleAlt')}
    >
      <ListContainer>
        {availableCases.map((caseItem) => (
          <ListItem key={caseItem.id} onClick={() => handleToggle(caseItem.id)}>
            <Checkbox checked={selectedCases.includes(caseItem.id)} />
            <span>{caseItem.title}</span>
          </ListItem>
        ))}
      </ListContainer>
      <Buttons>
        <Button
          size="middle"
          onClick={handleAdd}
          disabled={selectedCases.length === 0 || loading}
          loading={loading}
        >
          {t('addBtn')}
        </Button>
      </Buttons>
    </ModalContainerV2>
  );
};

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding: 24px 0;
  max-height: 500px;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 7px 0;
  cursor: pointer;
  gap: 16px;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;
const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 0 0 0;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column;
    margin-top: auto;
  }
`;
